import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { assessmentAPI } from '../../services/api';
import { getErrorMessage, logErrorForDeveloper } from '../../utils/errorHandler';

const reject = (context, error, fallback, rejectWithValue) => {
  logErrorForDeveloper(context, error);
  return rejectWithValue(getErrorMessage(error, fallback));
};

export const fetchAssessments = createAsyncThunk('assessments/fetchAll', async (params, { rejectWithValue }) => {
  try { const res = await assessmentAPI.fetchAssessments(params); return res.data; }
  catch (e) { return reject('assessments/fetchAll', e, 'We could not load assessments right now. Please try again.', rejectWithValue); }
});

export const createAssessment = createAsyncThunk('assessments/create', async (data, { rejectWithValue }) => {
  try { const res = await assessmentAPI.createAssessment(data); return res.data; }
  catch (error) { return reject('assessments/create', error, 'Unable to create the assessment. Please check your input and try again.', rejectWithValue); }
});

export const updateAssessment = createAsyncThunk('assessments/update', async ({ id, data }, { rejectWithValue }) => {
  try { const res = await assessmentAPI.updateAssessment(id, data); return res.data; }
  catch (error) { return reject('assessments/update', error, 'Unable to save your changes. Please try again.', rejectWithValue); }
});

export const deleteAssessment = createAsyncThunk('assessments/delete', async (id, { rejectWithValue }) => {
  try { await assessmentAPI.deleteAssessment(id); return id; }
  catch (error) { return reject('assessments/delete', error, 'Unable to delete this assessment right now. Please try again.', rejectWithValue); }
});

export const publishAssessment = createAsyncThunk('assessments/publish', async (id, { rejectWithValue }) => {
  try { const res = await assessmentAPI.publishAssessment(id); return res.data; }
  catch (error) { return reject('assessments/publish', error, 'Unable to publish this assessment. Please try again.', rejectWithValue); }
});

export const fetchAssessmentResults = createAsyncThunk('assessments/fetchResults', async (id, { rejectWithValue }) => {
  try { const res = await assessmentAPI.getAssessmentResults(id); return { assessmentId: id, results: res.data }; }
  catch (e) { return reject('assessments/fetchResults', e, 'We could not load results for this assessment.', rejectWithValue); }
});

export const fetchStudentResults = createAsyncThunk('assessments/fetchStudentResults', async (studentId, { rejectWithValue }) => {
  try { const res = await assessmentAPI.getStudentResults(studentId); return res.data; }
  catch (e) { return reject('assessments/fetchStudentResults', e, 'We could not load these results right now.', rejectWithValue); }
});

export const fetchStudentAssessments = createAsyncThunk('assessments/fetchStudentAssessments', async (studentId, { rejectWithValue }) => {
  try { const res = await assessmentAPI.getStudentAssessments(studentId); return res.data; }
  catch (e) { return reject('assessments/fetchStudentAssessments', e, 'We could not load assessments right now. Please try again.', rejectWithValue); }
});

export const fetchAssessmentRankings = createAsyncThunk('assessments/fetchRankings', async (id, { rejectWithValue }) => {
  try { const res = await assessmentAPI.getAssessmentRankings(id); return { assessmentId: id, rankings: res.data }; }
  catch (e) { return reject('assessments/fetchRankings', e, 'We could not load rankings for this assessment.', rejectWithValue); }
});

const initialState = {
  assessments: [],
  currentAssessment: null,
  questions: [],
  results: [],
  rankings: [],
  studentAssessments: [],
  studentResults: [],
  loading: false,
  error: null,
};

const assessmentSlice = createSlice({
  name: 'assessments',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    setCurrentAssessment: (state, action) => { state.currentAssessment = action.payload; },
    setQuestions: (state, action) => { state.questions = action.payload; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssessments.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAssessments.fulfilled, (state, action) => { state.loading = false; state.assessments = action.payload; })
      .addCase(fetchAssessments.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createAssessment.fulfilled, (state, action) => {
        const items = Array.isArray(action.payload) ? action.payload : [action.payload];
        items.forEach((a) => state.assessments.unshift(a));
      })
      .addCase(updateAssessment.fulfilled, (state, action) => {
        state.assessments = state.assessments.map((a) => a.id === action.payload.id ? action.payload : a);
        if (state.currentAssessment?.id === action.payload.id) state.currentAssessment = action.payload;
      })
      .addCase(deleteAssessment.fulfilled, (state, action) => {
        const deletedId = action.payload;
        const deleted = state.assessments.find((a) => a.id === deletedId);
        const groupId = deleted?.group_id;
        if (groupId) {
          state.assessments = state.assessments.filter((a) => a.group_id !== groupId);
        } else {
          state.assessments = state.assessments.filter((a) => a.id !== deletedId);
        }
      })
      .addCase(publishAssessment.fulfilled, (state, action) => {
        state.assessments = state.assessments.map((a) => a.id === action.payload.id ? action.payload : a);
        if (state.currentAssessment?.id === action.payload.id) state.currentAssessment = action.payload;
      })
      .addCase(fetchAssessmentResults.fulfilled, (state, action) => { state.results = action.payload.results || []; })
      .addCase(fetchStudentResults.fulfilled, (state, action) => { state.studentResults = action.payload; })
      .addCase(fetchStudentAssessments.fulfilled, (state, action) => { state.studentAssessments = action.payload; })
      .addCase(fetchAssessmentRankings.fulfilled, (state, action) => { state.rankings = action.payload.rankings || []; });
  },
});

export const { clearError, setCurrentAssessment, setQuestions } = assessmentSlice.actions;
export default assessmentSlice.reducer;
